<template>
  <div class="overview">
    <div class="overview--left">
      <div class="overview--selection" data-step="1">
        <InputSelect
          id="chosen_event"
          v-model="analyticsStore.chosenEventId"
          filter
          label="Select Event"
          :disabled="analyticsStore.loading"
          :options="dashboardEvents"
          optionLabel="name"
          optionValue="id"
          :showRequired="false"
        />
      </div>
      <div v-if="analyticsStore.chosenEventDetails" class="overview--details">
        <div><IconLocation /> {{ eventLocation }}</div>
        <div class="overview--date">
          <IconDate /> {{ chosenEventStart }} &nbsp; - &nbsp; <IconDate /> {{ chosenEventEnd }}
        </div>
      </div>
    </div>
    <div class="overview--right" data-step="2">
      <div class="overview--right-inner">
        <div class="overview--configure">
          <span><IconCalendarTop /></span>
          Event Configuration
        </div>
        <div class="overview--stats">
          <div class="overview--stat">
            <span>Zones</span>
            {{ analyticsStore.chosenEventDetails?.zones?.length || '-' }}
          </div>

          <div class="overview--stat">
            <span>Clusters</span>
            {{ analyticsStore.chosenEventDetails?.clusters?.length || '-' }}
          </div>
        </div>
      </div>
      <div class="overview--button">
        <Button
          severity="secondary"
          :link="`/events/${analyticsStore.chosenEventDetails?.id}`"
          :disabled="!analyticsStore.chosenEventDetails"
        >
          View Details
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAnalyticsStore } from '@/stores/analytics';
import { useEventsStore } from '@/stores/events';

const analyticsStore = useAnalyticsStore();
const eventsStore = useEventsStore();

import { computed } from 'vue';

import Button from '../global/Button.vue';
import InputSelect from '../inputs/InputSelect.vue';

import IconCalendarTop from '@/icons/analytics/IconCalendarTop.svg?component';
import IconDate from '@/icons/analytics/IconDate.svg?component';
import IconLocation from '@/icons/analytics/IconLocation.svg?component';

const dashboardEvents = computed(() => {
  return analyticsStore.reportableEvents.filter((event) => {
    return !event?.features?.some((f) => f.name === 'scorecard');
  });
});

const eventLocation = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    const locMatch = eventsStore.eventList?.location.find(
      (loc) => loc.id === analyticsStore.chosenEventDetails?.timezone_id,
    );
    return locMatch?.name || '';
  }
  return '';
});

const chosenEventStart = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    return analyticsStore.chosenEventDetails.event_start_str;
  }
  return '';
});

const chosenEventEnd = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    return analyticsStore.chosenEventDetails.event_end_str;
  }
  return '';
});
</script>

<style lang="scss">
.overview {
  align-items: stretch;
  background: variables.$primitives-color-border-100;
  border: 1px solid variables.$primitives-color-border-200;
  border-radius: variables.$sizing-xxs;
  margin-bottom: variables.$sizing-lg;

  @media screen and (min-width: 768px) {
    @include mixins.flex-center($horiz: space-between, $gap: variables.$sizing-xs);
  }
}

.overview--left {
  @include sizing.padding();
  border-right: 1px solid variables.$primitives-color-border-200;
  flex: 1;
  flex-basis: calc(60% - 1px);

  .overview--selection {
    padding-bottom: 1rem;
    padding-top: 1rem;

    @media screen and (min-width: 768px) {
      @include sizing.padding('lg', 0, 'xs', 0);
    }
  }

  .overview--details {
    @include mixins.flex-center($wrap: true);
    @include typography.font-body($size: sm, $weight: normal);
    justify-content: space-between;

    @media screen and (max-width: 768px) {
      gap: variables.$sizing-xs;
    }

    > div {
      @media screen and (min-width: 768px) {
        @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxxs);
      }
    }
  }
}

.overview--right {
  @include sizing.margin(0, 'md');
  @include sizing.padding('md', 0);
  border-top: 1px solid variables.$primitives-color-border-200;
  display: flex;
  flex: 0;
  flex-basis: 450px;

  @media screen and (min-width: 768px) {
    @include sizing.padding();
    border-top: 0;
  }

  .overview--right-inner {
    @include mixins.flex-center($horiz: flex-start, $wrap: true);
    flex: 0;
    flex-basis: 195px;
    gap: variables.$sizing-xs;

    @media screen and (min-width: 768px) {
      flex-basis: 225px;
    }
  }

  .overview--stats {
    @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-sm);
    flex: 0;
    flex-basis: 100%;
    margin-top: auto;
  }

  .overview--stat:first-of-type {
    border-right: 1px solid variables.$primitives-color-border-200;
    padding-right: variables.$sizing-sm;
  }

  .overview--stat {
    @include typography.font-display($size: xs, $weight: bold);
    color: variables.$primitives-color-tertiary-500;

    span {
      @include typography.font-body($size: sm, $weight: medium);
      color: variables.$primitives-color-tertiary-400;
      display: block;
      text-transform: uppercase;
    }
  }
}

.overview--button {
  margin-left: auto;

  @media screen and (min-width: 768px) {
    @include mixins.flex-center($horiz: flex-end);
    flex: 1;
  }
}

.overview--configure {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
  padding-bottom: 0.4rem;

  @media screen and (min-width: 768px) {
    padding-bottom: 0;
  }

  span {
    @include mixins.flex-center();
    background: variables.$primitives-color-background-100;
    border-radius: 50%;
    height: 40px;
    width: 40px;
  }
}
</style>
